import type { MedplumClient } from '@medplum/core';
import type { Appointment } from '@medplum/fhirtypes';
import type { useMedplum } from '@medplum/react-hooks';
import { CALENDAR_EVENT_CATEGORY_SYSTEM, remapAppointmentNoteTemplateId } from '@unify/fhir-model/constants';
import { getNoteTemplateId } from '@unify/fhir-model/resources/healthcare-service';
import type { NoteTemplateOption } from '../hooks';
import {
  embeddedOutcomeNoteQuestionnaire,
  OUTCOME_NOTE_TEMPLATE_IDS,
  type OutcomeNoteTemplateKind,
} from '../noteTemplates';
import { getOutcomeSpecificNoteTemplateKind } from './appointmentWorkflowStatus';

const TEMPLATE_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/questionnaire-template';
const TEMPLATE_CANONICAL_URL_PREFIX = 'https://unify.health/fhir/Questionnaire/';

type MedplumLike = MedplumClient | ReturnType<typeof useMedplum>;

export interface HealthcareServiceNoteTemplateResolution {
  templateRef: string;
  questionnaireId: string | null;
}

/** HealthcareService id stored on Appointment.serviceType (calendar catalog round-trip). */
export function getHealthcareServiceIdFromAppointment(appointment: Appointment): string | undefined {
  for (const serviceType of appointment.serviceType ?? []) {
    const coding = serviceType.coding?.find((c) => c.system === CALENDAR_EVENT_CATEGORY_SYSTEM);
    if (coding?.code?.trim()) return coding.code.trim();
  }
  return undefined;
}

/** Match a HealthcareService note-template ref to a loaded template library option. */
export function findNoteTemplateOptionForHealthcareServiceRef(
  templates: NoteTemplateOption[],
  templateRef: string,
  resolvedQuestionnaireId?: string | null,
): NoteTemplateOption | undefined {
  const ref = templateRef.trim();
  if (!ref) return undefined;
  const remapped = remapAppointmentNoteTemplateId(ref);

  if (resolvedQuestionnaireId) {
    const byResolvedId = templates.find((t) => t.id === resolvedQuestionnaireId);
    if (byResolvedId) return byResolvedId;
  }

  const byDirectId = templates.find((t) => t.id === ref || t.id === remapped);
  if (byDirectId) return byDirectId;

  return templates.find((t) => {
    const slugId = t.questionnaire.identifier?.find((i) => i.system === TEMPLATE_IDENTIFIER_SYSTEM)?.value;
    if (slugId === ref || slugId === remapped) return true;
    const urlSlug = t.questionnaire.url?.match(/\/Questionnaire\/([^/?#]+)/)?.[1];
    return urlSlug === ref || urlSlug === remapped;
  });
}

/** Resolve HealthcareService note-template-id to a Medplum Questionnaire id (mirrors AI bot). */
export async function resolveNoteTemplateFromHealthcareService(
  medplum: MedplumLike,
  healthcareServiceId: string,
): Promise<HealthcareServiceNoteTemplateResolution | null> {
  const hs = await medplum.readResource('HealthcareService', healthcareServiceId);
  const templateRef = getNoteTemplateId(hs);
  if (!templateRef?.trim()) return null;

  const remapped = remapAppointmentNoteTemplateId(templateRef.trim());
  if (!remapped) return null;
  const ref = remapped;
  try {
    const direct = await medplum.readResource('Questionnaire', ref);
    return { templateRef: ref, questionnaireId: direct.id ?? ref };
  } catch {
    // templateRef may be a slug identifier, not a Medplum id
  }

  try {
    const byIdentifier = await medplum.searchResources('Questionnaire', {
      identifier: `${TEMPLATE_IDENTIFIER_SYSTEM}|${ref}`,
      _count: '1',
    });
    if (byIdentifier[0]?.id) {
      return { templateRef: ref, questionnaireId: byIdentifier[0].id };
    }
  } catch {
    // fall through
  }

  try {
    const byUrl = await medplum.searchResources('Questionnaire', {
      url: `${TEMPLATE_CANONICAL_URL_PREFIX}${ref}`,
      _count: '1',
    });
    if (byUrl[0]?.id) {
      return { templateRef: ref, questionnaireId: byUrl[0].id };
    }
  } catch {
    // fall through
  }

  return { templateRef: ref, questionnaireId: null };
}

export function findOutcomeNoteTemplateOption(
  templates: NoteTemplateOption[],
  kind: OutcomeNoteTemplateKind,
): NoteTemplateOption | undefined {
  const slug = OUTCOME_NOTE_TEMPLATE_IDS[kind];
  return templates.find(
    (t) =>
      t.id === slug
      || t.questionnaire.identifier?.some((i) => i.value === slug)
      || t.questionnaire.url?.includes(`/Questionnaire/${slug}`),
  );
}

/** Embedded definitions — used when outcome templates are not yet in the Template Library / Medplum. */
export function embeddedOutcomeNoteTemplateOption(
  kind: OutcomeNoteTemplateKind,
): NoteTemplateOption {
  const questionnaire = embeddedOutcomeNoteQuestionnaire(kind);
  return {
    id: questionnaire.id!,
    title: questionnaire.title ?? 'Untitled Template',
    questionnaire,
  };
}

/** Server library match, else embedded fallback so attendance-driven switching works before upload. */
export function resolveOutcomeNoteTemplateOption(
  templates: NoteTemplateOption[],
  kind: OutcomeNoteTemplateKind,
): NoteTemplateOption {
  return findOutcomeNoteTemplateOption(templates, kind) ?? embeddedOutcomeNoteTemplateOption(kind);
}

/**
 * Pick the default appointment-note template id.
 * Priority: outcome templates → HealthcareService note-template-id match.
 * Does not fall back to templates[0] — an incompatible first result must never win.
 */
export async function resolveDefaultAppointmentNoteTemplateId(
  medplum: MedplumLike,
  appointment: Appointment | null | undefined,
  templates: NoteTemplateOption[],
  options?: {
    ignoreAppointmentOutcome?: boolean;
    /** Broader library used only to resolve HS note-template-id when filtered list misses it. */
    allTemplates?: NoteTemplateOption[];
  },
): Promise<string | null> {
  if (!appointment) return null;

  const outcomeKind = options?.ignoreAppointmentOutcome
    ? undefined
    : getOutcomeSpecificNoteTemplateKind(appointment);
  if (outcomeKind) {
    return resolveOutcomeNoteTemplateOption(templates, outcomeKind).id;
  }

  const healthcareServiceId = getHealthcareServiceIdFromAppointment(appointment);
  if (!healthcareServiceId) return null;

  const searchPools = [templates, options?.allTemplates ?? []].filter((pool) => pool.length > 0);
  if (searchPools.length === 0) return null;

  try {
    const resolved = await resolveNoteTemplateFromHealthcareService(medplum, healthcareServiceId);
    if (resolved) {
      for (const pool of searchPools) {
        const match = findNoteTemplateOptionForHealthcareServiceRef(
          pool,
          resolved.templateRef,
          resolved.questionnaireId,
        );
        if (match) return match.id;
      }
    }
  } catch {
    // No deterministic default
  }

  return null;
}
